import {useEffect, useState, useCallback} from "react";
import axiosInstance from "../api/axiosInstance";
import ConfirmModal from "../components/ConfirmModal";
import PageGuideCard from "../components/PageGuideCard";
import RecipeCard from "../components/RecipeCard";
import SuggestedUsers from "../components/SuggestedUsers";
import {useNavigate, useSearchParams} from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { appendDismissedGuide, PAGE_GUIDE_KEYS } from "../lib/pageGuides";
import { toast } from "../lib/toast";


const RecipeList = () => {
  const [recipes, setRecipes] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const view = searchParams.get("view") || "public";
  const selectedCategory = searchParams.get("category") || "";
  const selectedCollection = searchParams.get("collection") || "";
  const selectedCollectionName = searchParams.get("collection_name") || "";
  const selectedTag = searchParams.get("tag") || "";
  const selectedDiet = searchParams.get("diet_preference") || "";
  const page = parseInt(searchParams.get("page")) || 1;

  const [searchQuery, setSearchQuery] = useState("");
  const [sortOption, setSortOption] = useState("");
  const [totalCount, setTotalCount] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrevious, setHasPrevious] = useState(false);
  const [cartRecipeIds, setCartRecipeIds] = useState([]);
  const [trendingRecipes, setTrendingRecipes] = useState([]);
  const [trendingLoading, setTrendingLoading] = useState(false);
  const [filterOptions, setFilterOptions] = useState({ categories: [], diets: [], popular_tags: [] });
  const [showRenameBookmark, setShowRenameBookmark] = useState(false);
  const [bookmarkNameDraft, setBookmarkNameDraft] = useState("");
  const [bookmarkActionLoading, setBookmarkActionLoading] = useState(false);
  const [showDeleteBookmarkConfirm, setShowDeleteBookmarkConfirm] = useState(false);
  const navigate = useNavigate();
  const { currentUser, updateCurrentUser } = useAuth();

  // Continuously save scroll position as user scrolls
  useEffect(() => {
    const main = document.querySelector("main");
    if (!main) return;
    const onScroll = () => sessionStorage.setItem("recipeList-scroll", String(main.scrollTop));
    main.addEventListener("scroll", onScroll, { passive: true });
    return () => main.removeEventListener("scroll", onScroll);
  }, []);

  const fetchCartIds = useCallback(async () => {
    try {
      const res = await axiosInstance.get("/api/cart/");
      setCartRecipeIds((res.data.items || []).map((item) => item.recipe.id));
    } catch {
      setCartRecipeIds([]);
    }
  }, []);

  useEffect(() => {
    fetchCartIds();
    const handler = () => fetchCartIds();
    window.addEventListener("cart-changed", handler);
    return () => window.removeEventListener("cart-changed", handler);
  }, [fetchCartIds]);

  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const params = { visibility: view };
        if (view === "bookmarks" && selectedCollection) {
          params.collection = selectedCollection;
        }
        const res = await axiosInstance.get("/api/recipes/filters/", { params });
        setFilterOptions(res.data);
      } catch (err) {
        console.error("Failed to fetch filters:", err);
      }
    };
    fetchFilters();
  }, [view, selectedCollection]);

  useEffect(() => {
    if (view !== "discover") {
      setTrendingRecipes([]);
      setTrendingLoading(false);
      return;
    }

    let cancelled = false;

    const fetchTrending = async () => {
      setTrendingLoading(true);
      try {
        const res = await axiosInstance.get("/api/recipes/trending/");
        if (!cancelled) {
          setTrendingRecipes(Array.isArray(res.data) ? res.data : []);
        }
      } catch (err) {
        if (!cancelled) {
          console.error("Failed to load trending recipes:", err);
          setTrendingRecipes([]);
        }
      } finally {
        if (!cancelled) {
          setTrendingLoading(false);
        }
      }
    };

    fetchTrending();

    return () => {
      cancelled = true;
    };
  }, [view]);

  useEffect(() => {
    const fetchRecipes = async () => {
      try {
        const params = { visibility: view, page };
        if (selectedCollection) params.collection = selectedCollection;
        if (searchQuery) params.search = searchQuery;
        if (selectedCategory) params.category = selectedCategory;
        if (selectedTag) params.tag = selectedTag;
        if (selectedDiet) params.diet_preference = selectedDiet;
        if (sortOption) params.ordering = sortOption;

        const res = await axiosInstance.get("/api/recipes/", { params });
        setRecipes(res.data.results);
        setTotalCount(res.data.count);
        setHasNext(!!res.data.next);
        setHasPrevious(!!res.data.previous);

        // Restore scroll after content renders
        const saved = sessionStorage.getItem("recipeList-scroll");
        if (saved) {
          sessionStorage.removeItem("recipeList-scroll");
          requestAnimationFrame(() => {
            const main = document.querySelector("main");
            if (main) main.scrollTop = parseInt(saved);
          });
        }
      } catch (err) {
        console.error("Failed to load recipes", err);
      }
    };
    fetchRecipes();
  }, [view, searchQuery, selectedCategory, selectedCollection, selectedTag, selectedDiet, sortOption, page]);

  const setPage = useCallback((newPage) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (newPage <= 1) {
        next.delete("page");
      } else {
        next.set("page", String(newPage));
      }
      return next;
    });
  }, [setSearchParams]);

  const pageTitle =
    view === "bookmarks"
      ? selectedCollectionName
        ? `${selectedCollectionName} Recipes`
        : "Bookmarked"
      : view === "public"
      ? "Public Recipes"
      : "Private Recipes";

  const handleDismissOnboarding = useCallback(async (guideKey) => {
    try {
      await updateCurrentUser({
        onboarding_dismissed_pages: appendDismissedGuide(currentUser, guideKey),
      });
    } catch (err) {
      console.error("Failed to dismiss onboarding:", err);
    }
  }, [currentUser, updateCurrentUser]);

  useEffect(() => {
    setShowRenameBookmark(false);
    setBookmarkNameDraft(selectedCollectionName);
    setShowDeleteBookmarkConfirm(false);
  }, [selectedCollection, selectedCollectionName]);

  const handleStartRenameBookmark = () => {
    setBookmarkNameDraft(selectedCollectionName);
    setShowRenameBookmark(true);
  };

  const handleCancelRenameBookmark = () => {
    setBookmarkNameDraft(selectedCollectionName);
    setShowRenameBookmark(false);
  };

  const handleSaveBookmarkRename = async () => {
    const trimmedName = bookmarkNameDraft.trim();
    if (!selectedCollection || !trimmedName || bookmarkActionLoading) return;
    if (trimmedName === selectedCollectionName) {
      setShowRenameBookmark(false);
      return;
    }

    setBookmarkActionLoading(true);
    try {
      await axiosInstance.patch(`/api/bookmarks/collections/${selectedCollection}/`, {
        name: trimmedName,
      });
      toast.success("Bookmark renamed.");
      window.dispatchEvent(new Event("bookmark-changed"));
      navigate(
        `/recipes?view=bookmarks&collection=${selectedCollection}&collection_name=${encodeURIComponent(trimmedName)}`
      );
      setShowRenameBookmark(false);
    } catch (err) {
      if (err.response?.status === 409) {
        toast.error("Bookmark already exists.");
      } else {
        console.error("Failed to rename bookmark", err);
        toast.error("Failed to rename bookmark.");
      }
    } finally {
      setBookmarkActionLoading(false);
    }
  };

  const handleDeleteBookmark = async () => {
    if (!selectedCollection || bookmarkActionLoading) return;

    setBookmarkActionLoading(true);
    try {
      await axiosInstance.delete(`/api/bookmarks/collections/${selectedCollection}/`);
      toast.success("Bookmark deleted.");
      window.dispatchEvent(new Event("bookmark-changed"));
      setShowDeleteBookmarkConfirm(false);
      navigate("/recipes?view=bookmarks");
    } catch (err) {
      console.error("Failed to delete bookmark", err);
      toast.error("Failed to delete bookmark.");
    } finally {
      setBookmarkActionLoading(false);
    }
  };

  return (
    <div className="flex-1 p-6 max-w-6xl mx-auto">
      {view === "discover" ? (
        <>
          <h1 className="type-page-title mb-6">Discover</h1>

          <PageGuideCard
            onboarding={currentUser}
            guideKey={PAGE_GUIDE_KEYS.RECIPES_DISCOVER}
            title="This is your discover page"
            description="Explore public recipes from across CartNom here, including your own public recipes. Use it to find new ideas, follow great cooks, and see what is worth saving or cooking next."
            onDismiss={() => handleDismissOnboarding(PAGE_GUIDE_KEYS.RECIPES_DISCOVER)}
          />

          <div className="mb-8">
            <h2 className="type-section-title mb-3">Suggested Users</h2>
            <SuggestedUsers />
          </div>

          {trendingRecipes.length > 0 && (
            <div className="mb-10 border-t dark:border-gray-700 pt-6">
              <div className="mb-4">
                <h2 className="type-section-title">Trending This Week</h2>
                <p className="type-muted mt-2">
                  Recipes getting the most recent attention across CartNom.
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6">
                {trendingRecipes.slice(0, 4).map((recipe) => (
                  <RecipeCard
                    key={`trending-${recipe.id}`}
                    recipe={recipe}
                    cartRecipeIds={cartRecipeIds}
                  />
                ))}
              </div>
            </div>
          )}

          <div className="border-t dark:border-gray-700 pt-6">
            <h2 className="type-section-title mb-2">Browse All Recipes</h2>
            {trendingLoading && (
              <p className="type-muted mb-4">Loading trending recipes...</p>
            )}
          </div>
        </>
      ) : view === "bookmarks" ? (
        <div className="mb-6">
          {selectedCollection && (
            <button
              onClick={() => navigate("/recipes?view=bookmarks")}
              className="mb-3 text-sm font-medium text-blue-600 transition hover:text-blue-700"
            >
              ← Back to all
            </button>
          )}

          <div className="flex items-end justify-between gap-4">
            <div>
              {selectedCollection && showRenameBookmark ? (
                <div className="space-y-3">
                  <input
                    type="text"
                    value={bookmarkNameDraft}
                    onChange={(e) => setBookmarkNameDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleSaveBookmarkRename();
                      } else if (e.key === "Escape") {
                        handleCancelRenameBookmark();
                      }
                    }}
                    className="w-full max-w-md rounded-xl border border-slate-200 px-3 py-2 text-2xl font-semibold text-slate-900 outline-none focus:border-blue-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                    autoFocus
                  />
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={handleSaveBookmarkRename}
                      disabled={!bookmarkNameDraft.trim() || bookmarkActionLoading}
                      className="rounded-lg bg-blue-500 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-blue-600 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {bookmarkActionLoading ? "Saving..." : "Save"}
                    </button>
                    <button
                      type="button"
                      onClick={handleCancelRenameBookmark}
                      disabled={bookmarkActionLoading}
                      className="rounded-lg bg-slate-100 px-3 py-1.5 text-sm font-medium text-slate-600 transition hover:bg-slate-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <h1 className="type-page-title">{pageTitle}</h1>
                  <p className="type-muted mt-2">
                    {selectedCollectionName
                      ? `Recipes saved in this bookmark.`
                      : "Browse everything you’ve saved across all bookmarks."}
                  </p>
                </>
              )}
            </div>
            <div className="flex items-center gap-2">
              {selectedCollection && !showRenameBookmark && (
                <>
                  <button
                    type="button"
                    onClick={handleStartRenameBookmark}
                    className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-600 transition hover:border-slate-300 hover:text-slate-800 dark:border-gray-700 dark:text-gray-300 dark:hover:text-white"
                  >
                    Rename
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowDeleteBookmarkConfirm(true)}
                    className="rounded-lg border border-red-200 px-3 py-1.5 text-sm font-medium text-red-500 transition hover:border-red-300 hover:text-red-600 dark:border-red-900/40 dark:hover:border-red-800"
                  >
                    Delete
                  </button>
                </>
              )}
              <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-500 dark:bg-gray-800 dark:text-gray-300">
                {totalCount} {totalCount === 1 ? "recipe" : "recipes"}
              </span>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-between mb-6">
          <h1 className="type-page-title">{pageTitle}</h1>

          {view !== "bookmarks" && (
            <button
              className="type-button bg-blue-500 hover:bg-blue-500 text-white px-4 py-2 rounded shadow transition transform hover:scale-105"
              onClick={() => navigate("/recipes/create")}
            >
              + Create New Recipe
            </button>
          )}
        </div>
      )}

      {view === "public" && (
        <PageGuideCard
          onboarding={currentUser}
          guideKey={PAGE_GUIDE_KEYS.RECIPES_PUBLIC}
          title="This is where your public recipes live"
          description="Create public recipes here when you want other people to discover, cook, and fork them. Public recipes help your profile feel alive and make CartNom more useful right away."
          ctaLabel="Create your first public recipe"
          ctaHref="/recipes/create?view=public"
          onDismiss={() => handleDismissOnboarding(PAGE_GUIDE_KEYS.RECIPES_PUBLIC)}
        />
      )}

      {view === "private" && (
        <PageGuideCard
          onboarding={currentUser}
          guideKey={PAGE_GUIDE_KEYS.RECIPES_PRIVATE}
          title="This is where your private recipes live"
          description="Keep recipes here when you want them only for yourself. Private recipes still work with your pantry, planner, and shopping flow without being visible to anyone else."
          ctaLabel="Create a private recipe"
          ctaHref="/recipes/create?view=private"
          onDismiss={() => handleDismissOnboarding(PAGE_GUIDE_KEYS.RECIPES_PRIVATE)}
        />
      )}

      {view === "bookmarks" && (
        <PageGuideCard
          onboarding={currentUser}
          guideKey={PAGE_GUIDE_KEYS.RECIPES_SAVED}
          title="This is where your saved recipes stay organized"
          description="Create, rename, or delete bookmarks here, then collect recipes you want to keep close, revisit later, or cook again without hunting for them. To add a recipe, click the + on a recipe card and save it into the bookmark you want."
          ctaLabel="Browse recipes to save"
          ctaHref="/recipes?view=discover"
          onDismiss={() => handleDismissOnboarding(PAGE_GUIDE_KEYS.RECIPES_SAVED)}
        />
      )}

      {/* Search, filters, and sort */}
      <div className="flex flex-wrap gap-3 mb-6 items-center">
        <input
          type="text"
          placeholder="Search recipes..."
          value={searchQuery}
          onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
          className="border px-3 py-1 rounded-md w-48 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
        />

        <select
          value={selectedCategory}
          onChange={(e) => {
            const next = new URLSearchParams(searchParams);
            if (e.target.value) next.set("category", e.target.value); else next.delete("category");
            next.delete("page");
            setSearchParams(next);
          }}
          className="border px-2 py-1 rounded-md text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
        >
          <option value="">All Categories</option>
          {filterOptions.categories.map((cat) => (
            <option key={cat} value={cat}>{cat}</option>
          ))}
        </select>

        <select
          value={selectedDiet}
          onChange={(e) => {
            const next = new URLSearchParams(searchParams);
            if (e.target.value) next.set("diet_preference", e.target.value); else next.delete("diet_preference");
            next.delete("page");
            setSearchParams(next);
          }}
          className="border px-2 py-1 rounded-md text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
        >
          <option value="">All Diets</option>
          {filterOptions.diets.map((diet) => (
            <option key={diet} value={diet}>{diet.charAt(0).toUpperCase() + diet.slice(1)}</option>
          ))}
        </select>

        <select
          value={selectedTag}
          onChange={(e) => {
            const next = new URLSearchParams(searchParams);
            if (e.target.value) next.set("tag", e.target.value); else next.delete("tag");
            next.delete("page");
            setSearchParams(next);
          }}
          className="border px-2 py-1 rounded-md text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
        >
          <option value="">All Tags</option>
          {filterOptions.popular_tags.map((tag) => (
            <option key={tag.id} value={tag.name}>#{tag.name}</option>
          ))}
        </select>

        <select
          value={sortOption}
          onChange={(e) => { setSortOption(e.target.value); setPage(1); }}
          className="border px-2 py-1 rounded-md text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
        >
          <option value="">Sort By</option>
          <option value="prep_time">Prep Time</option>
          <option value="cook_time">Cook Time</option>
          <option value="servings">Servings</option>
          <option value="difficulty">Difficulty</option>
        </select>

        {(selectedCategory || selectedDiet || selectedTag) && (
          <button
            onClick={() => {
              const next = new URLSearchParams(searchParams);
              next.delete("category");
              next.delete("diet_preference");
              next.delete("tag");
              next.delete("page");
              setSearchParams(next);
            }}
            className="type-button text-xs text-blue-500 hover:text-blue-600 transition"
          >
            Clear filters
          </button>
        )}
      </div>

      {view === "bookmarks" && recipes.length === 0 ? (
        <div className="rounded-[1.6rem] border border-slate-200/80 bg-white px-8 py-10 text-center shadow-[0_20px_45px_-38px_rgba(15,23,42,0.32)] dark:border-gray-700 dark:bg-gray-800/60">
          <h2 className="type-section-title">
            {selectedCollectionName ? "This bookmark is empty" : "No bookmarked recipes yet"}
          </h2>
          <p className="type-muted mt-3 max-w-xl mx-auto">
            {selectedCollectionName
              ? "Browse recipes and click the + button on any recipe card to save it into this bookmark."
              : "Start building your saved space by adding recipes from Discover into bookmarks you want to revisit."}
          </p>
          <button
            onClick={() => navigate("/recipes?view=discover")}
            className="mt-5 rounded-lg bg-blue-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-600"
          >
            Browse recipes
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
          {recipes.map((recipe) => (
            <RecipeCard
              key={recipe.id}
              recipe={recipe}
              cartRecipeIds={cartRecipeIds}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {Math.ceil(totalCount / 12) > 1 && (
        <div className="flex justify-center items-center gap-4 mt-8">
          <button
            onClick={() => setPage(page - 1)}
            disabled={!hasPrevious}
            className="px-4 py-2 bg-gray-200 dark:bg-gray-700 dark:text-gray-300 rounded disabled:opacity-50"
          >
            Previous
          </button>
          <span className="text-sm text-gray-600 dark:text-gray-400">
            Page {page} of {Math.ceil(totalCount / 12)}
          </span>
          <button
            onClick={() => setPage(page + 1)}
            disabled={!hasNext}
            className="px-4 py-2 bg-gray-200 dark:bg-gray-700 dark:text-gray-300 rounded disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}

      {showDeleteBookmarkConfirm && (
        <ConfirmModal
          title="Delete bookmark?"
          message={`This will remove ${selectedCollectionName || "this bookmark"} and its saved recipe links.`}
          confirmText={bookmarkActionLoading ? "Deleting..." : "Delete"}
          onConfirm={handleDeleteBookmark}
          onCancel={() => setShowDeleteBookmarkConfirm(false)}
        />
      )}
    </div>
  );
};

export default RecipeList;
